import { mutatorAction } from "satcheljs";
import { IUserTeamSettingsEntity } from "sh-models";
import UserTeamSettingsStore from "../store";
import { ObservableMap } from "mobx";

/**
 * Sets UserTeamSettings for team and rebuilds derived caches (e.g., muteScheduleGroupStatusByTeamAndGroup).
 * Called by DataProvider after loading from network/database or after API updates.
 *
 * CACHE CONSISTENCY NOTES:
 * This mutator clears and rebuilds muteScheduleGroupStatusByTeamAndGroup from scratch.
 * This approach is safe because:
 * 1. Optimistic updates use setMuteScheduleGroupStatus directly (doesn't trigger full rebuild)
 * 2. This mutator only runs after network/database loads, which should match optimistic state
 * 3. UserTeamSettings is user-scoped (no concurrent modification by other users possible)
 * 4. If brief inconsistency occurs (e.g., concurrent tabs), eTag concurrency control prevents data loss
 *
 * ⚠️ WARNING - Cache Drift Prevention:
 * DO NOT mutate mutedScheduleGroupIds directly outside this mutator or the toggle orchestrator.
 * Direct mutations will cause cache drift between mutedScheduleGroupIds (source of truth) and
 * muteScheduleGroupStatusByTeamAndGroup (derived cache). Always use proper actions/orchestrators
 * to ensure both primary storage and derived cache stay in sync.
 *
 * Pattern matches other stores (TagStore, UserStore) that rebuild derived caches from source of truth.
 */
const setUserTeamSettingsForTeam = mutatorAction(
    "SET_USER_TEAM_SETTINGS_FOR_TEAM",
    (teamId: string, userTeamSettings: IUserTeamSettingsEntity) => {
        const store = UserTeamSettingsStore();

        // Update primary storage
        store.userTeamSettingsByTeam.set(teamId, userTeamSettings);

        // Ensure team has muteStatus map
        if (!store.muteScheduleGroupStatusByTeamAndGroup.has(teamId)) {
            store.muteScheduleGroupStatusByTeamAndGroup.set(teamId, new ObservableMap<string, boolean>());
        }

        // Rebuild cache from mutedScheduleGroupIds
        const mutedIds = new Set(userTeamSettings?.mutedScheduleGroupIds || []);
        const muteScheduleGroupMap = store.muteScheduleGroupStatusByTeamAndGroup.get(teamId);

        // Clear old entries
        muteScheduleGroupMap.clear();

        // Mark muted groups as true (groups default to unmuted if not in map)
        mutedIds.forEach((groupId: string) => {
            muteScheduleGroupMap.set(groupId, true);
        });
    }
);

export default setUserTeamSettingsForTeam;
